import React, { useState, useRef, useCallback } from "react";
import ExperienceHeader from "./ExperienceHeader";
import PlanTabs, { PLAN_TABS } from "./PlanTabs";
import ExistingPlansSection from "./ExistingPlansSection";
import CustomizePlanSection, { BENEFIT_TENANT_ID } from "./CustomizePlanSection";
import CustomBenefitPanel from "./CustomBenefitPanel";
import BuyPlan from "./PlanPurchase/BuyPlan";

const PlanExperience = () => {
  // Ready-made plans is the default view. Only the active tab's section is
  // ever rendered - switching tabs unmounts the previous section (and, with
  // it, whatever benefits were selected there) instead of stacking every
  // section on the page. That's what keeps a user's selection confined to a
  // single plan mode at a time: you can't have benefits picked in "Build
  // your own" and "Custom Benefit" simultaneously, because only one of
  // those sections ever exists in the tree.
  const [activeTab, setActiveTab] = useState(PLAN_TABS.READY_MADE);

  const [openPopup, setOpenPopup] = useState(false);
  const [planCode, setPlanCode] = useState("");
  const [tenantId, setTenantId] = useState("");
  const [showMember, setShowMember] = useState(false);
  const [isRider, setIsRider] = useState(false);
  const [memberCount, setMemberCount] = useState(0);
  const [buyBenefits, setBuyBenefits] = useState([]);

  const onPurchaseCompleteRef = useRef(null);

  const openBuy = useCallback(
    ({
      tenantId: buyTenantId,
      planCode: buyPlanCode = "",
      members = 0,
      isRider: rider = true,
      showMember: member = false,
      benefits = [],
      onPurchaseComplete,
    }) => {
      onPurchaseCompleteRef.current = onPurchaseComplete ?? null;
      setTenantId(buyTenantId);
      setPlanCode(buyPlanCode);
      setIsRider(rider);
      setShowMember(member);
      setMemberCount(members);
      setBuyBenefits(benefits);
      setOpenPopup(true);
    },
    []
  );

  const handlePurchaseComplete = useCallback(() => {
    onPurchaseCompleteRef.current?.();
  }, []);

  // Buying straight from the Custom Benefit tab's category panel.
  const handleCategoryContinue = useCallback(
    (chosenBenefits) => {
      if (!chosenBenefits || chosenBenefits.length === 0) return;
      openBuy({
        tenantId: BENEFIT_TENANT_ID,
        planCode: "",
        members: 0,
        isRider: false,
        showMember: false,
        benefits: chosenBenefits,
      });
    },
    [openBuy]
  );

  const handleTabChange = useCallback((tab) => {
    setActiveTab(tab);
  }, []);

  return (
    <main className="font-montserrat">
      <section className="relative w-full bg-[#F7F7FA] px-4 pt-[100px] pb-16 sm:px-6 md:pt-[130px] lg:px-8 lg:pt-[110px]">
        {/* Subtle header glow - decorative only, not a hero. Clipped in its
            own overflow-hidden box (rather than on the whole section) since
            overflow-hidden on an ancestor of the sticky tab bar below would
            silently break position: sticky. */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-x-0 top-0 h-[380px] overflow-hidden"
        >
          <div className="absolute -top-28 left-1/2 h-[380px] w-[760px] -translate-x-1/2 rounded-full bg-gradient-to-b from-[#6D28D9]/[0.06] to-transparent blur-3xl" />
        </div>

        <div className="relative mx-auto max-w-7xl">
          <ExperienceHeader />

          {/* Sticky directly below the fixed site header, so the tabs stay
              reachable while scrolling through whichever section is open. */}
          <div className="sticky top-[76px] z-30 -mx-4 mt-6 bg-[#F7F7FA]/95 px-4 py-3 backdrop-blur-sm sm:top-[80px]">
            <PlanTabs active={activeTab} onChange={handleTabChange} />
          </div>

          <div className="mt-8">
            {activeTab === PLAN_TABS.READY_MADE && (
              <div id="panel-ready-made" aria-labelledby="tab-ready-made">
                <ExistingPlansSection
                  onBuy={openBuy}
                  onBrowseBenefits={() => handleTabChange(PLAN_TABS.BUILD_YOUR_OWN)}
                  isActive
                />
              </div>
            )}

            {activeTab === PLAN_TABS.BUILD_YOUR_OWN && (
              <div id="panel-build-your-own" aria-labelledby="tab-build-your-own">
                <CustomizePlanSection onBuy={openBuy} isActive />
              </div>
            )}

            {activeTab === PLAN_TABS.CUSTOM_BENEFIT && (
              <div id="panel-custom_benefit" aria-labelledby="tab-custom_benefit">
                <CustomBenefitPanel
                  open
                  tenantId={BENEFIT_TENANT_ID}
                  onClose={() => handleTabChange(PLAN_TABS.READY_MADE)}
                  onContinue={handleCategoryContinue}
                />
              </div>
            )}
          </div>
        </div>
      </section>

      <BuyPlan
        planCode={planCode}
        tenantId={tenantId}
        showPopUp={openPopup}
        setShowPopUp={setOpenPopup}
        isRider={isRider}
        showMember={showMember}
        memberCount={memberCount}
        benefits={buyBenefits}
        onPurchaseComplete={handlePurchaseComplete}
      />
    </main>
  );
};

export default PlanExperience;
